import React, { useEffect, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import { decode } from "html-entities";
import "./Filters.scss";
import { useApi, useUtilities } from "../../hooks";
import { DropDown } from "../dropdown/DropDown";
import "react-datepicker/dist/react-datepicker.css";
import { TriangleTextured, MapPinTextured } from "../images";
interface FiltersProps {
  filterValues?: {
    eventType: string;
    when: string;
    where?: Date;
  };
  changeHandler: (field: string, value: any) => void;
  resetTrigger?: number;
}

export const Filters: React.FC<FiltersProps> = ({
  filterValues,
  changeHandler,
  resetTrigger,
}) => {
  const { fetchEventTypes, fetchWhereAutocomplete } = useApi();
  const { formatDateISO } = useUtilities();
  const [eventOptions, setEventOptions] = useState([]);
  const [rawWhereText, setRawWhereText] = useState("");
  const [whereOptions, setWhereOptions] = useState<any[]>([]);
  const [open, setOpen] = useState(false);
  const [eventTypeOpen, setEventTypeOpen] = useState(false);
  const whereDropdown = useRef<HTMLDivElement>(null);
  const eventTypeDropdown = useRef<HTMLDivElement>(null);

  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const [startDate, endDate] = dateRange;

  // Helper function to strip HTML tags from text
  const stripHtmlTags = (html: string): string => {
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
  };


  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        whereDropdown.current &&
        !whereDropdown.current?.contains(event.target as Node)
      ) {
        setWhereOptions([]);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        eventTypeDropdown.current &&
        !eventTypeDropdown.current?.contains(event.target as Node)
      ) {
        setEventTypeOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const init = async () => {
      const results = await fetchEventTypes();
      setEventOptions(results);
    };
    init();
  }, [fetchEventTypes]);

  // Clear the where input text when the where filter value is cleared
  useEffect(() => {
    if (!filterValues?.where) {
      setRawWhereText("");
      setWhereOptions([]);
    }
  }, [filterValues?.where]);

  // Reset DatePicker state when resetTrigger changes (e.g., Clear Filters clicked)
  useEffect(() => {
    if (resetTrigger) {
      setDateRange([null, null]);
    }
  }, [resetTrigger]);

  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser.");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        changeHandler("where", {
          label: "Current Location",
          latitude,
          longitude,
          locationType: "current",
        });
        setRawWhereText("Current Location");
        setWhereOptions([]);
      },
      (error) => {
        alert(`Error getting location: ${error.message}`);
      }
    );
  };

  const popupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="filters">
      <div className="filters__group">
        <label htmlFor="eventType">Event Type</label>
        <div className="filters__input-wrapper">
          <div className={`filters__input-wrapper__inner ${eventTypeOpen ? 'filters__input-wrapper__inner--open' : ''}`}>
            <input
              id="eventType"
              readOnly
              type="text"
              placeholder="Select event type"
              onFocus={() => setEventTypeOpen(true)}
              onChange={(e) => changeHandler("when", e.target.value)}
              value={decode((filterValues?.eventType as any)?.label ?? "")}
            />
            <TriangleTextured className="filters__input-wrapper__input-icon" />
          </div>
          <div ref={eventTypeDropdown}>
            <DropDown
              options={eventOptions.map((option: any) => {
                return {
                  value: option.term_id,
                  label: option.name,
                };
              })}
              onSelect={(value) => {
                const option: any = eventOptions.find(
                  (option: any) => option.term_id === value
                );
                changeHandler(
                  "eventType",
                  option
                    ? {
                        value: option.term_id,
                        label: option.name,
                      }
                    : { value: "", label: "" }
                );
                setEventTypeOpen(false);
              }}
              hide={!eventTypeOpen}
            />
          </div>
        </div>
      </div>

      <div className="filters__group">
        <label htmlFor="when">When</label>
        <div className="filters__input-wrapper">
          <div className={`filters__input-wrapper__inner ${open ? 'filters__input-wrapper__inner--open' : ''}`}>
            <input
              id="when"
              readOnly
              type="text"
              placeholder="Select dates"
              value={(() => {
                const whenValue = filterValues?.when;

                if (!whenValue) {
                  return "";
                }

                // Check for our "This Week" object structure
                if (typeof whenValue === 'object' && 'label' in whenValue && 'startDate' in whenValue) {
                  return (whenValue as { label: string }).label;
                }

                // Assume it's a Date object (or something that can be parsed into one)
                // from "Today", "Tomorrow", or DatePicker.
                // We need to be careful here due to the original FiltersProps.when typing as string,
                // even though EventSearch passes a Date or our custom object.
                const d = new Date(whenValue as any); // Use 'as any' to bypass strict Date constructor typing here
                if (d instanceof Date && !isNaN(d.getTime())) { // Check if it's a valid Date
                  return d.toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  });
                }
                
                return ""; // Fallback for invalid or unhandled date types
              })()}
              onFocus={() => setOpen(true)}
              onChange={(e) => changeHandler("when", e.target.value)}
            />
            <TriangleTextured className="filters__input-wrapper__input-icon" />
          </div>
          {open && (
            <div className="datepicker" ref={popupRef}>
              <div className="datepicker__inner">
                <DatePicker
                  selectsRange
                  startDate={startDate}
                  endDate={endDate}
                  minDate={new Date()}
                  onChange={(update: [Date | null, Date | null]) => {
                      setDateRange(update);
                  }}
                  inline
                  calendarClassName="datepicker__calendar"
                />
                <div className="datepicker__controls">
                  <button
                    className="datepicker__button datepicker__button--cancel"
                    onClick={() => {
                      changeHandler("when", "");
                      setDateRange([null, null]);
                      setOpen(false);
                    }}
                  >
                    Cancel
                  </button>
                      <button
                          className="datepicker__button datepicker__button--select"
                          onClick={() => {
                              if (startDate) {
                                  const finalEnd = endDate || startDate; // fallback to start if no end chosen
                                  const label = startDate.getTime() === finalEnd.getTime() 
                                    ? startDate.toLocaleDateString()
                                    : `${startDate.toLocaleDateString()} - ${finalEnd.toLocaleDateString()}`;
                                  changeHandler("when", {
                                    startDate: formatDateISO(startDate),
                                    endDate: formatDateISO(finalEnd),
                                    label: label
                                  });
                              }
                              setOpen(false);
                          }}
                      >
                          Select
                      </button>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="filters__quick-links">
          <a
            href="/"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              changeHandler("when", new Date());
            }}
          >
            Today
          </a>
          <a
            href="/"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              changeHandler("when", new Date(new Date().getTime() + 86400000));
            }}
          >
            Tomorrow
          </a>
          <a
            href="/"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              // Calculate start and end of the current week (Monday to Sunday)
              const today = new Date();
              const currentDay = today.getDay(); // 0 for Sunday, 1 for Monday, ..., 6 for Saturday
              const startDate = new Date(today);
              // Adjust to Monday of the current week
              startDate.setDate(today.getDate() - (currentDay === 0 ? 6 : currentDay - 1));
              
              const endDate = new Date(startDate);
              endDate.setDate(startDate.getDate() + 6); // Sunday of the current week

              changeHandler("when", {
                startDate: formatDateISO(startDate),
                endDate: formatDateISO(endDate),
                label: "This Week" // For display purposes if needed
              });
            }}
          >
            This Week
          </a>
        </div>
      </div>

      <div className="filters__group">
        <label htmlFor="where">Where</label>
        <div ref={whereDropdown} className="filters__input-wrapper">
          <div className="filters__input-wrapper__inner">
            <input
              id="where"
              type="text"
              placeholder="Search by city or region"
              value={rawWhereText}
              onFocus={() => {}}
              onChange={(e) => {
                setRawWhereText(e.target.value);


                const autocomplete = async () => {
                  const results = await fetchWhereAutocomplete(e.target.value);
                  const transformedOptions = Array.isArray(results)
                    ? results.map((item) => {
                        return {
                          value: item.id,
                          label: item.text,
                          latitude: item.location?.latitude,
                          longitude: item.location?.longitude,
                          locationType: item.type,
                          locationId: item.locationId,
                        };
                      })
                    : [];
                  if (rawWhereText) {
                    setWhereOptions(transformedOptions);
                  }
                };
                if (e.target.value) {
                  autocomplete();
                } else {
                  setWhereOptions([]);
                  changeHandler("where", undefined);
                }
              }}
            />
          </div>
          <DropDown
            options={whereOptions}
            onSelect={(value) => {
              const option = whereOptions.find(
                (option) => option.value === value
              );
              // Strip HTML tags from the label before setting it in the input
              const cleanLabel = option?.label
                ? stripHtmlTags(option.label)
                : "";
              setRawWhereText(cleanLabel);
              changeHandler("where", option);
              setWhereOptions([]);
            }}
            hide={!whereOptions.length}
          />
        </div>
        <a
          href="/"
          className="filters__location"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleUseCurrentLocation();
          }}
        >
          <MapPinTextured />
          <span>Use current location</span>
        </a>
      </div>
    </div>
  );
};
